/* mm-offer-box: custom set builder. Pack cards pick how many, the tray holds each item, a swatch switches its finish. */
(function () {
  if (customElements.get('mm-offer-box')) return;

  const sum = (list) => list.reduce((a, b) => a + b, 0);

  class MMOfferBox extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('[data-config]');
      if (!configEl) return;
      try {
        this.cfg = JSON.parse(configEl.textContent);
      } catch (e) {
        return;
      }
      this.strings = this.cfg.strings || {};
      this.variants = new Map(this.cfg.variants.map((variant) => [variant.id, variant]));
      this.tray = this.querySelector('[data-tray]');
      this.filledTpl = this.querySelector('template[data-slot-filled]');
      this.emptyTpl = this.querySelector('template[data-slot-empty]');
      this.items = (this.cfg.start || []).filter((id) => this.variants.has(id));
      if (!this.items.length && this.cfg.variants[0]) this.items = [this.cfg.variants[0].id];
      this.ended = !!this.cfg.ended;
      this.busy = false;
      this.lastTotal = null;

      this.onClick = this.onClick.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onSubmitCapture = this.onSubmitCapture.bind(this);
      this.addEventListener('click', this.onClick);
      // Capture on window so the theme form and the cart app never see the click: this box adds the whole set itself.
      window.addEventListener('click', this.onClickCapture, true);
      window.addEventListener('submit', this.onSubmitCapture, true);

      this.takeOverForm();
      this.startTimer();
      this.refresh();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('click', this.onClickCapture, true);
      window.removeEventListener('submit', this.onSubmitCapture, true);
      clearInterval(this.interval);
    }

    get form() {
      return document.getElementById(this.dataset.form);
    }
    get button() {
      const form = this.form;
      return form ? form.querySelector('[type="submit"]') : null;
    }

    /* ---- state ---- */
    onClick(event) {
      const target = event.target;
      const pack = target.closest('[data-pack]');
      if (pack) {
        this.setCount(Number(pack.dataset.pack));
        return;
      }
      const add = target.closest('[data-add]');
      if (add) {
        const count = Number(add.dataset.add);
        this.setCount(count);
        const fresh = this.tray.children[count - 1];
        const focus = fresh && (fresh.querySelector('[data-on]') || fresh.querySelector('[data-swatch]'));
        if (focus) focus.focus({ preventScroll: true });
        return;
      }
      const swatch = target.closest('[data-swatch]');
      if (swatch && !swatch.disabled) {
        const slot = swatch.closest('[data-slot]');
        if (slot) this.setItem(Number(slot.dataset.slot), Number(swatch.dataset.swatch));
      }
    }

    firstAvailable() {
      const open = this.cfg.variants.find((variant) => variant.available);
      return (open || this.cfg.variants[0]).id;
    }

    // What the set would hold at a given size: the current picks, then the default fill.
    itemsFor(count) {
      const list = this.items.slice(0, count);
      while (list.length < count) {
        const id = (this.cfg.fill || [])[list.length];
        const variant = this.variants.get(id);
        list.push(variant && variant.available ? id : list[list.length - 1] || this.firstAvailable());
      }
      return list;
    }

    setCount(count) {
      const next = Math.max(1, Math.min(this.cfg.max, count));
      const from = this.items.length;
      if (next === from) return;
      this.items = this.itemsFor(next);
      this.buildTray(next > from ? from : null);
      this.refresh();
    }

    setItem(index, id) {
      const variant = this.variants.get(id);
      if (!variant || !variant.available || this.items[index] === id) return;
      this.items[index] = id;
      this.refresh();
      const slot = this.tray.children[index];
      const image = slot ? slot.querySelector('[data-slot-image]') : null;
      if (image) this.animate(image, 'is-swap');
      this.showInGallery(variant);
    }

    animate(el, name) {
      el.classList.remove(name);
      void el.offsetWidth;
      el.classList.add(name);
      el.addEventListener('animationend', () => el.classList.remove(name), { once: true });
    }

    flagText(tier) {
      if (!tier || this.ended || !(tier.discounted > 0) || !(tier.percent > 0)) return '';
      return tier.percent >= 100 ? this.strings.free || '' : (this.strings.off || '').replace('[percent]', tier.percent);
    }

    compute(items) {
      const prices = items.map((id) => this.variants.get(id).price);
      const full = sum(prices);
      const tier = this.cfg.tiers[items.length] || null;
      const text = this.flagText(tier);
      const count = text ? Math.min(tier.discounted, items.length) : 0;
      // Shopify discounts the cheapest items of a buy X get Y deal. On a tie the later items take it.
      const picked = new Set(
        prices
          .map((price, i) => ({ price, i }))
          .sort((a, b) => a.price - b.price || b.i - a.i)
          .slice(0, count)
          .map((entry) => entry.i)
      );
      const paid = prices.map((price, i) => (picked.has(i) ? price - Math.round((price * tier.percent) / 100) : price));
      const total = sum(paid);
      return {
        items,
        tier,
        full,
        total,
        off: full - total,
        each: Math.round(total / items.length),
        flags: prices.map((_, i) => (picked.has(i) ? text : '')),
      };
    }

    /* ---- drawing ---- */
    buildTray(newFrom) {
      if (!this.tray || !this.filledTpl || !this.emptyTpl) return;
      const fragment = document.createDocumentFragment();
      for (let i = 0; i < this.cfg.max; i++) {
        const filled = i < this.items.length;
        const node = (filled ? this.filledTpl : this.emptyTpl).content.firstElementChild.cloneNode(true);
        if (filled) {
          node.dataset.slot = i;
          if (newFrom !== null && newFrom !== undefined && i >= newFrom) node.classList.add('is-new');
        }
        fragment.appendChild(node);
      }
      this.tray.replaceChildren(fragment);
    }

    refresh() {
      if (!this.tray) return;
      const count = this.items.length;
      const filled = this.tray.querySelectorAll('[data-slot]').length;
      if (this.tray.children.length !== this.cfg.max || filled !== count) this.buildTray(null);

      const state = this.compute(this.items);
      Array.from(this.tray.children).forEach((slot, i) => {
        if (i < count) this.drawFilled(slot, i, state);
        else this.drawEmpty(slot, i + 1);
      });
      this.drawPacks(count);
      this.drawTotal(state);
      this.syncForm(state);
      this.labelButton(state);
    }

    drawFilled(slot, index, state) {
      const variant = this.variants.get(this.items[index]);
      slot.dataset.slot = index;
      const image = slot.querySelector('[data-slot-image]');
      if (image) {
        image.hidden = !variant.image;
        if (variant.image && image.getAttribute('src') !== variant.image) image.src = variant.image;
      }
      const name = slot.querySelector('[data-slot-name]');
      if (name) name.textContent = variant.name;
      const group = slot.querySelector('.mm-ob__swatches');
      if (group) group.setAttribute('aria-label', (this.strings.item || '').replace('[number]', index + 1));
      slot.querySelectorAll('[data-swatch]').forEach((swatch) => {
        const on = Number(swatch.dataset.swatch) === variant.id;
        swatch.toggleAttribute('data-on', on);
        swatch.setAttribute('aria-checked', String(on));
      });
      const flag = slot.querySelector('[data-flag]');
      if (flag) {
        flag.hidden = !state.flags[index];
        flag.textContent = state.flags[index];
      }
    }

    drawEmpty(slot, count) {
      const tier = this.cfg.tiers[count] || null;
      slot.dataset.add = count;
      const name = slot.querySelector('[data-slot-name]');
      if (name) name.textContent = tier ? (this.strings.upgrade || '').replace('[name]', tier.title) : '';
      const flag = slot.querySelector('[data-flag]');
      const text = this.flagText(tier);
      if (flag) {
        flag.hidden = !text;
        flag.textContent = text;
      }
    }

    drawPacks(count) {
      this.querySelectorAll('[data-pack]').forEach((pack) => {
        const size = Number(pack.dataset.pack);
        const on = size === count;
        pack.classList.toggle('is-active', on);
        pack.setAttribute('aria-checked', String(on));
        const state = this.compute(this.itemsFor(size));
        const total = pack.querySelector('[data-pack-total]');
        if (total) total.textContent = this.money(state.total);
        const compare = pack.querySelector('[data-pack-compare]');
        if (compare) {
          compare.hidden = state.off <= 0;
          compare.textContent = this.money(state.full);
        }
      });
      if (this.ended) this.querySelectorAll('[data-deal-only]').forEach((el) => (el.hidden = true));
    }

    drawTotal(state) {
      const s = this.strings;
      const name = this.querySelector('[data-set-name]');
      if (name) name.textContent = (s.setName || '').replace('[name]', state.tier ? state.tier.title : '').trim();
      const each = this.querySelector('[data-each]');
      if (each) {
        each.hidden = state.off <= 0 || state.items.length < 2 || !s.each;
        each.textContent = (s.each || '').replace('[amount]', this.money(state.each));
      }
      const save = this.querySelector('[data-save]');
      if (save) {
        const percent = state.full > 0 ? Math.round((state.off / state.full) * 100) : 0;
        save.hidden = state.off <= 0 || !s.save;
        save.textContent = (s.save || '').replace('[amount]', this.money(state.off, true)).replace('[percent]', percent + '%');
      }
      const compare = this.querySelector('[data-compare]');
      if (compare) {
        compare.hidden = state.off <= 0;
        compare.textContent = this.money(state.full);
      }
      const total = this.querySelector('[data-total]');
      if (total) {
        total.textContent = this.money(state.total);
        if (this.lastTotal !== null && this.lastTotal !== state.total) {
          this.animate(total, 'is-bump');
          if (save && !save.hidden) this.animate(save, 'is-bump');
        }
      }
      this.lastTotal = state.total;
    }

    // Show the picked finish in the main gallery, only while the gallery is on screen (Dawn scrolls to it otherwise).
    showInGallery(variant) {
      if (!variant.media) return;
      const gallery = (this.closest('.product') || document).querySelector('media-gallery');
      if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
      const viewer = gallery.querySelector('[id^="GalleryViewer"]');
      if (!viewer) return;
      const rect = viewer.getBoundingClientRect();
      if (rect.top < 0 || rect.top > window.innerHeight * 0.6) return;
      gallery.setActiveMedia(gallery.id.replace('MediaGallery-', '') + '-' + variant.media, false);
    }

    /* ---- theme form ---- */
    takeOverForm() {
      // This box is the variant picker and the quantity selector: hide the theme's own if they are still on the template.
      const info = this.closest('product-info') || document;
      info.querySelectorAll('variant-selects, variant-radios, .product-form__quantity').forEach((el) => el.classList.add('mm-ob-replaced'));

      const button = this.button;
      if (!button || this.dataset.mainButton !== 'true') return;
      button.classList.add('mm-ob-main-button');
      // Same width as the box, instead of the theme's fixed button width.
      const buttons = button.closest('.product-form__buttons');
      if (buttons) buttons.classList.add('mm-ob-buttons');
      if (this.dataset.buttonBg) button.style.setProperty('--color-button', this.dataset.buttonBg);
      if (this.dataset.buttonText) button.style.setProperty('--color-button-text', this.dataset.buttonText);
      button.style.setProperty('--alpha-button-background', '1');
    }

    syncForm(state) {
      const form = this.form;
      if (!form || !state.items.length) return;
      const idInput = form.querySelector('[name="id"]');
      if (idInput) idInput.value = state.items[0];
      // form.id would return the <input name="id"> inside the form, so use the id this box was given.
      const formId = this.dataset.form;
      let qty = document.querySelector('input[name="quantity"][form="' + formId + '"]') || form.querySelector('input[name="quantity"]');
      if (!qty) {
        qty = document.createElement('input');
        qty.type = 'hidden';
        qty.name = 'quantity';
        qty.setAttribute('form', formId);
        this.appendChild(qty);
      }
      qty.value = state.items.length;
    }

    labelButton(state) {
      if (this.dataset.showTotal !== 'true') return;
      const button = this.button;
      if (!button || button.disabled) return;
      const label = button.querySelector('span');
      if (!label) return;
      if (!this.baseLabel) this.baseLabel = label.textContent.trim();
      label.textContent = this.baseLabel + ' • ' + this.money(state.total);
    }

    grouped() {
      const map = new Map();
      this.items.forEach((id) => map.set(id, (map.get(id) || 0) + 1));
      return Array.from(map, ([id, quantity]) => ({ id, quantity }));
    }

    /* ---- add to cart ---- */
    onClickCapture(event) {
      const form = this.form;
      const target = event.target;
      if (!form || !target || !target.closest) return;

      const submit = target.closest('[type="submit"]');
      if (submit && submit.form === form) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (submit.getAttribute('aria-disabled') !== 'true') this.addToCart();
        return;
      }

      // Express checkout can only carry one variant. With a mix picked, send the whole set straight to checkout instead.
      const pay = target.closest('.shopify-payment-button');
      if (!pay || !form.contains(pay)) return;
      const items = this.grouped();
      if (items.length < 2) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.href = this.root() + 'cart/' + items.map((item) => item.id + ':' + item.quantity).join(',');
    }

    onSubmitCapture(event) {
      if (event.target !== this.form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.addToCart();
    }

    root() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    addToCart() {
      if (this.busy) return;
      const items = this.grouped();
      if (!items.length) return;

      // Monster Cart Upsell replaces the theme cart on this store. Dawn's own cart UI is the fallback.
      const monster = document.querySelector('#monster-cart-wrapper');
      const useMonster = monster && typeof window.monster_refresh === 'function';
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = { items };
      if (!useMonster && cart && cart.getSectionsToRender) {
        body.sections = cart.getSectionsToRender().map((section) => section.id).join(',');
        body.sections_url = window.location.pathname;
      }

      this.setLoading(true);
      this.showError(false);
      fetch(this.root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((res) => {
          if (res.status) {
            this.showError(res.description || res.message || 'Could not add to cart');
            return null;
          }
          if (useMonster) {
            return Promise.resolve(window.monster_refresh()).then(() => {
              if (monster.classList.contains('mu_openned')) return;
              const bag = document.querySelector('#cart-icon-bubble');
              if (bag) bag.click();
            });
          }
          const last = res.items && res.items[res.items.length - 1];
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mm-offer-box', productVariantId: last && last.variant_id, cartData: res });
          }
          if (cart && cart.renderContents && last && res.sections) {
            try {
              cart.renderContents(Object.assign({}, last, { sections: res.sections }));
              if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
              return null;
            } catch (e) {
              /* cart UI out of sync: fall through to the cart page, the items are in the cart */
            }
          }
          window.location.href = (window.routes && window.routes.cart_url) || this.root() + 'cart';
          return null;
        })
        .catch(() => this.showError('Could not add to cart'))
        .finally(() => this.setLoading(false));
    }

    setLoading(on) {
      this.busy = on;
      const button = this.button;
      if (!button) return;
      button.classList.toggle('loading', on);
      if (on) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !on);
    }

    showError(message) {
      const form = this.form;
      const productForm = form ? form.closest('product-form') : null;
      if (productForm && typeof productForm.handleErrorMessage === 'function') productForm.handleErrorMessage(message || false);
    }

    /* ---- countdown ---- */
    startTimer() {
      const end = Number(this.cfg.end) * 1000;
      if (!end || this.ended) return;
      const timer = this.querySelector('[data-timer]');
      const digits = timer ? timer.querySelector('[data-timer-digits]') : null;
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const left = end - Date.now();
        if (left <= 0) {
          this.endOffer();
          return;
        }
        if (!digits) return;
        const s = Math.floor(left / 1000);
        const parts = [
          [Math.floor(s / 86400), 'd'],
          [Math.floor((s % 86400) / 3600), 'h'],
          [Math.floor((s % 3600) / 60), 'm'],
          [s % 60, 's'],
        ];
        if (parts[0][0] === 0) parts.shift();
        digits.innerHTML = parts.map((p) => '<span class="mm-ob__chip">' + pad(p[0]) + '<small>' + p[1] + '</small></span>').join('');
        digits.setAttribute('aria-label', parts.map((p) => p[0] + p[1]).join(' '));
        timer.hidden = false;
      };
      tick();
      this.interval = setInterval(tick, 1000);
    }

    // The deal is over: every option goes back to full price, so the box never shows a price the cart will not honor.
    endOffer() {
      clearInterval(this.interval);
      this.ended = true;
      const urgency = this.querySelector('[data-urgency]');
      if (urgency) urgency.hidden = true;
      this.refresh();
    }

    /* ---- money ---- */
    money(cents, trim) {
      const format = this.cfg.money || '${{amount}}';
      const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
      const kind = match ? match[1] : 'amount';
      const group = (decimals, thousands, decimal) => {
        const parts = (cents / 100).toFixed(decimals).split('.');
        const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts[1] ? whole + decimal + parts[1] : whole;
      };
      let out;
      if (kind === 'amount_no_decimals') out = group(0, ',', '.');
      else if (kind === 'amount_with_comma_separator') out = group(2, '.', ',');
      else if (kind === 'amount_no_decimals_with_comma_separator') out = group(0, '.', ',');
      else if (kind === 'amount_with_apostrophe_separator') out = group(2, "'", '.');
      else if (kind === 'amount_with_space_separator') out = group(2, ' ', ',');
      else if (kind === 'amount_no_decimals_with_space_separator') out = group(0, ' ', ',');
      else out = group(2, ',', '.');
      if (trim) out = out.replace(/[.,]00$/, '');
      return match ? format.replace(match[0], out) : out;
    }
  }

  customElements.define('mm-offer-box', MMOfferBox);
})();
